import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Trash2 } from "lucide-react";

import { useTran } from "@/hooks";
import { showToast } from "@/lib/utils/toast";
import { Button } from "@/components/ui/button";
import { InputField } from "@/components/common";

import {
  POINTS_ENABLED_TYPES,
  sanitizeOptionsForType,
} from "../utils/survey-form";

const QuestionOverridesForm = ({ question, onSave, onCancel }) => {
  const { t } = useTran();
  const [overrides, setOverrides] = useState({
    questionText: "",
    description: "",
    points: 0,
    options: [],
    questionType: "",
  });

  useEffect(() => {
    if (!question) return;
    setOverrides({
      questionText: question.questionText,
      description: question.description,
      points: POINTS_ENABLED_TYPES.includes(question.questionType)
        ? question.points
        : 0,
      options: sanitizeOptionsForType(question.questionType, question.options),
      questionType: question.questionType,
    });
  }, [question]);

  const handleAddOption = () => {
    const currentCount = overrides.options?.length || 0;
    if (currentCount >= 5) {
      showToast.warning(t("Maximum of 5 options allowed."));
      return;
    }
    const nextIndex = currentCount + 1;
    setOverrides((prev) => ({
      ...prev,
      options: [
        ...(prev.options || []),
        {
          value: `option${nextIndex}`,
          label: `Option ${nextIndex}`,
          points: 0,
        },
      ].slice(0, 5),
    }));
  };

  const handleUpdateOptionLabel = (index, newLabel) => {
    setOverrides((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) =>
        i === index ? { ...opt, label: newLabel } : opt,
      ),
    }));
  };

  const handleUpdateOptionPoints = (index, newPoints) => {
    const value = Number(newPoints);
    if (value < 1) {
      showToast.warning(t("Minimum point is 1"));
      return;
    }
    if (value > 5) {
      showToast.warning(t("Maximum point is 5"));
      return;
    }

    setOverrides((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) =>
        i === index ? { ...opt, points: value } : opt,
      ),
    }));
  };

  const handleDeleteOption = (index) => {
    setOverrides((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const isPointsEnabled = POINTS_ENABLED_TYPES.includes(overrides.questionType);

  return (
    <div className="space-y-4 overflow-y-auto p-5">
      <InputField
        label={t("Question Text")}
        value={overrides.questionText}
        onChange={(event) =>
          setOverrides((prev) => ({
            ...prev,
            questionText: event.target.value,
          }))
        }
      />
      <InputField
        label={t("Description")}
        value={overrides.description || ""}
        onChange={(event) =>
          setOverrides((prev) => ({
            ...prev,
            description: event.target.value,
          }))
        }
      />
      {isPointsEnabled ? (
        <div className="space-y-2 border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="ml-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("Options & Points")}
              </p>
              <p className="ml-2 text-[11px] text-muted-foreground">
                {t("Assign system points per option.")}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleAddOption}
              variant="secondary"
            >
              <Plus className="mr-1 h-4 w-4" />
              {t("Add Option")}
            </Button>
          </div>

          <div className="space-y-1">
            {overrides.options && overrides.options.length > 0 ? (
              <AnimatePresence>
                {overrides.options.map((option, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-2 rounded-radius border border-border bg-muted/40 p-3"
                  >
                    <span className="min-w-6 text-xs font-semibold text-muted-foreground">
                      {index + 1}.
                    </span>
                    <InputField
                      placeholder={t("Option label")}
                      value={option.label}
                      onChange={(event) =>
                        handleUpdateOptionLabel(index, event.target.value)
                      }
                      className="flex-1 space-y-0"
                    />
                    <InputField
                      type="number"
                      min="1"
                      max="5"
                      placeholder={t("Points")}
                      value={option.points}
                      onChange={(event) =>
                        handleUpdateOptionPoints(index, event.target.value)
                      }
                      className="w-24 space-y-0"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteOption(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </motion.div>
                ))}
              </AnimatePresence>
            ) : (
              <p className="py-2 text-center text-xs text-muted-foreground">
                {t('No options added yet. Click "Add Option" to get started.')}
              </p>
            )}
          </div>
        </div>
      ) : null}

      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button variant="secondary" onClick={() => onSave(overrides)}>
          {t("Add to Survey")}
        </Button>
      </div>
    </div>
  );
};

export default QuestionOverridesForm;
